/** Bestand + relatief pad (map/bestand.ext) zoals gesleept of gekozen. */
export interface TeUploaden {
  relativePath: string;
  file: File;
}

/** Een gesleepte map die op een cd lijkt (D1): wordt één zip. */
export interface CdMap {
  mapnaam: string;
  /**
   * Pad van de cd-map in de drop ("CD", of "Patient/CD1" voor een sub-cd).
   * Daaronder staat hij in het register, zodat hervatten twee cd's met
   * dezelfde mapnaam niet samenvoegt (A2-4). De zip heet <mapnaam>.zip.
   */
  bronPad: string;
  bestanden: TeUploaden[];
}

export const CD_MIN_BESTANDEN = 50;
export const CD_MIN_AANDEEL = 0.8;

const SYSTEEMBESTANDEN = new Set([".ds_store", "thumbs.db", "desktop.ini"]);
const naamVan = (pad: string) =>
  (pad.replace(/\\/g, "/").split("/").pop() ?? "").toLowerCase();

/** Wat het besturingssysteem zelf in een map zet; nooit in een cd-zip (anders verschilt een tweede poging). */
export function isSysteembestand(pad: string): boolean {
  const naam = naamVan(pad);
  return SYSTEEMBESTANDEN.has(naam) || naam.startsWith("._");
}

/** DICOMDIR, of minstens 50 bestanden waarvan minstens 80 % zonder extensie of .dcm. */
export function lijktOpCd(paden: string[]): boolean {
  const namen = paden.filter((p) => !isSysteembestand(p)).map(naamVan);
  if (namen.includes("dicomdir")) return true;
  if (namen.length < CD_MIN_BESTANDEN) return false;
  const dicom = namen.filter(
    (n) => !n.includes(".") || n.endsWith(".dcm"),
  ).length;
  return dicom / namen.length >= CD_MIN_AANDEEL;
}

const segmentenVan = (i: TeUploaden) =>
  i.relativePath.replace(/\\/g, "/").split("/").filter(Boolean);
const isDicomdir = (i: TeUploaden) => naamVan(i.relativePath) === "dicomdir";

/** Groepeert op het segment met index `diepte` (alleen paden die dieper gaan). */
function groepeerOp(
  items: TeUploaden[],
  diepte: number,
): Map<string, TeUploaden[]> {
  const groepen = new Map<string, TeUploaden[]>();
  for (const i of items) {
    const seg = segmentenVan(i);
    if (seg.length <= diepte + 1) continue;
    const lijst = groepen.get(seg[diepte]);
    if (lijst) lijst.push(i);
    else groepen.set(seg[diepte], [i]);
  }
  return groepen;
}

/**
 * Splitst een drop in cd-mappen en de rest. Alleen een gesleepte map kan een
 * cd zijn: per bovenste map wordt gekeken. Staat DICOMDIR in die map zelf (of
 * lijkt hij zonder DICOMDIR op een cd), dan is de hele map één cd. Staat
 * DICOMDIR dieper, dan wordt elke directe submap met een DICOMDIR (waar ook
 * daaronder) of die zelf op een cd lijkt één cd, en gaat de rest van de map
 * als gewone bestanden (L7, A2-3).
 * Losse bestanden, ook losse .dcm-bestanden, blijven gewone uploads. De rest
 * houdt zijn volgorde.
 */
export function splitsCdMappen(items: TeUploaden[]): {
  cds: CdMap[];
  rest: TeUploaden[];
} {
  const cds: CdMap[] = [];
  const inCd = new Set<TeUploaden>();
  const maakCd = (
    mapnaam: string,
    bronPad: string,
    lijst: TeUploaden[],
    afknippen: number,
  ) => {
    lijst.forEach((i) => inCd.add(i));
    cds.push({
      mapnaam,
      bronPad,
      // Paden beginnen met de cd-map zelf, ook als die een submap was.
      bestanden: lijst
        .filter((i) => !isSysteembestand(i.relativePath))
        .map((i) =>
          afknippen === 0
            ? i
            : {
                relativePath: segmentenVan(i).slice(afknippen).join("/"),
                file: i.file,
              },
        ),
    });
  };
  groepeerOp(items, 0).forEach((lijst, mapnaam) => {
    const echt = lijst.filter((i) => !isSysteembestand(i.relativePath));
    const opWortel = echt.some(
      (i) => isDicomdir(i) && segmentenVan(i).length === 2,
    );
    if (opWortel) return maakCd(mapnaam, mapnaam, lijst, 0);
    if (echt.some(isDicomdir)) {
      groepeerOp(lijst, 1).forEach((sub, submap) => {
        // Ook een submap zonder DICOMDIR die op een cd lijkt (A2-3).
        if (sub.some(isDicomdir) || lijktOpCd(sub.map((i) => i.relativePath))) {
          maakCd(submap, `${mapnaam}/${submap}`, sub, 1);
        }
      });
      return;
    }
    if (lijktOpCd(lijst.map((i) => i.relativePath))) {
      maakCd(mapnaam, mapnaam, lijst, 0);
    }
  });
  return { cds, rest: items.filter((i) => !inCd.has(i)) };
}

/**
 * Namen voor de controle op een andere patiënt (trein 51, L8): de mapnaam van
 * de cd en de bestanden direct in de cd-map, zoals bij losse bestanden. Dieper
 * staan de DICOM-beelden; die namen zeggen niets over de patiënt.
 */
export function namenVoorNaamcontrole(cd: CdMap): string[] {
  const bovenin = cd.bestanden
    .filter((b) => segmentenVan(b).length === 2)
    .map((b) => b.file.name);
  return [cd.mapnaam, ...bovenin];
}
